import React, { useState } from 'react';
import {
  ChevronDownIcon,
  ChevronRightIcon,
  ExclamationTriangleIcon,
} from '@heroicons/react/24/outline';
import { Badge } from '../ui/Badge';
import { UsageBar } from './UsageBar';
import {
  USAGE_STATUS_BADGE,
  USAGE_STATUS_LABELS,
  formatLicenseExpiry,
  getUsagePercent,
  getUsageStatus,
} from '../../utils/usage';

const LICENSE_STATUS_LABELS = {
  active: 'Activa',
  expired: 'Expirada',
  suspended: 'Suspendida',
  cancelled: 'Cancelada',
};

const LICENSE_STATUS_BADGE = {
  active: 'success',
  expired: 'danger',
  suspended: 'warning',
  cancelled: 'danger',
};

const UsageStatusBadge = ({ seats, installed }) => {
  const status = getUsageStatus(seats, installed);
  const label = USAGE_STATUS_LABELS[status];
  if (!label) return null;
  return (
    <Badge variant={USAGE_STATUS_BADGE[status]} size="sm">
      {label}
    </Badge>
  );
};

const th = 'px-3 py-2 text-xs font-medium text-gray-500 uppercase tracking-wide';
const num = 'px-3 py-2.5 text-right tabular-nums whitespace-nowrap';

const CodesTable = ({ codes }) => (
  <div className="overflow-x-auto rounded-md border border-gray-200 bg-white">
    <table className="min-w-full divide-y divide-gray-100 text-xs">
      <thead className="bg-gray-50">
        <tr>
          <th className={`${th} text-left`}>Código de licencia</th>
          <th className={`${th} text-left`}>Estado</th>
          <th className={`${th} text-left`}>Vence</th>
          <th className={`${th} text-right`}>Contratadas</th>
          <th className={`${th} text-right`}>Instaladas</th>
          <th className={`${th} text-right`}>Libres</th>
          <th className={`${th} text-left`}>Uso</th>
        </tr>
      </thead>
      <tbody className="divide-y divide-gray-100">
        {codes.map((code) => (
          <tr key={code.license_id}>
            <td className="px-3 py-2 font-mono text-gray-900 whitespace-nowrap">
              <span className="inline-flex items-center gap-1">
                {code.license_key}
                {code.mismatch && (
                  <ExclamationTriangleIcon
                    className="h-4 w-4 text-warning-500"
                    title="El contador de instalaciones de esta licencia no coincide con los equipos registrados"
                  />
                )}
              </span>
            </td>
            <td className="px-3 py-2 whitespace-nowrap">
              <Badge variant={LICENSE_STATUS_BADGE[code.status] || 'secondary'} size="sm">
                {LICENSE_STATUS_LABELS[code.status] || code.status}
              </Badge>
            </td>
            <td className="px-3 py-2 text-gray-700 whitespace-nowrap">{formatLicenseExpiry(code)}</td>
            <td className="px-3 py-2 text-right tabular-nums">{code.seats}</td>
            <td className="px-3 py-2 text-right tabular-nums">{code.installed}</td>
            <td className="px-3 py-2 text-right tabular-nums">{code.free}</td>
            <td className="px-3 py-2">
              <div className="flex items-center gap-2">
                <UsageBar seats={code.seats} installed={code.installed} className="min-w-[110px]" />
                <UsageStatusBadge seats={code.seats} installed={code.installed} />
              </div>
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

export const ClientUsageTable = ({ clients, totals }) => {
  const [expandedClients, setExpandedClients] = useState(() => new Set());
  const [expandedSoftware, setExpandedSoftware] = useState(() => new Set());

  const toggle = (setter, key) =>
    setter((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });

  return (
    <div className="overflow-x-auto -mx-1">
      <table className="min-w-full divide-y divide-gray-200 text-sm">
        <thead className="bg-gray-50">
          <tr>
            <th className={`${th} text-left`}>Cliente</th>
            <th className={`${th} text-right`}>Software</th>
            <th className={`${th} text-right`}>Códigos</th>
            <th className={`${th} text-right`}>Licencias contratadas</th>
            <th className={`${th} text-right`}>Instaladas</th>
            <th className={`${th} text-right`}>Libres</th>
            <th className={`${th} text-left`}>Uso</th>
            <th className={`${th} text-left`}>Estado</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-100 bg-white">
          {clients.map((client) => {
            const clientOpen = expandedClients.has(client.client_id);
            const ClientChevron = clientOpen ? ChevronDownIcon : ChevronRightIcon;

            return (
              <React.Fragment key={client.client_id}>
                <tr
                  className="hover:bg-gray-50 cursor-pointer"
                  onClick={() => toggle(setExpandedClients, client.client_id)}
                >
                  <td className="px-3 py-2.5">
                    <button
                      type="button"
                      aria-expanded={clientOpen}
                      className="flex items-center gap-2 text-left rounded focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500"
                    >
                      <ClientChevron className="h-4 w-4 text-gray-400 flex-shrink-0" />
                      <span className="min-w-0">
                        <span className="block font-medium text-gray-900 truncate">{client.client_name}</span>
                        {client.company && client.company !== client.client_name && (
                          <span className="block text-xs text-gray-500 truncate">{client.company}</span>
                        )}
                      </span>
                    </button>
                  </td>
                  <td className={num}>{client.software_count}</td>
                  <td className={num}>{client.license_codes}</td>
                  <td className={`${num} font-medium text-gray-900`}>{client.seats}</td>
                  <td className={num}>{client.installed}</td>
                  <td className={num}>{client.free}</td>
                  <td className="px-3 py-2.5">
                    <UsageBar seats={client.seats} installed={client.installed} />
                  </td>
                  <td className="px-3 py-2.5 whitespace-nowrap">
                    <UsageStatusBadge seats={client.seats} installed={client.installed} />
                  </td>
                </tr>

                {clientOpen &&
                  client.software.map((sw) => {
                    const swKey = `${client.client_id}-${sw.software_id}`;
                    const swOpen = expandedSoftware.has(swKey);
                    const SoftwareChevron = swOpen ? ChevronDownIcon : ChevronRightIcon;

                    return (
                      <React.Fragment key={swKey}>
                        <tr
                          className="bg-gray-50/60 hover:bg-gray-100 cursor-pointer"
                          onClick={() => toggle(setExpandedSoftware, swKey)}
                        >
                          <td className="py-2 pr-3 pl-9">
                            <button
                              type="button"
                              aria-expanded={swOpen}
                              className="flex items-center gap-2 text-left text-gray-800 rounded focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500"
                            >
                              <SoftwareChevron className="h-4 w-4 text-gray-400 flex-shrink-0" />
                              {sw.software_name}
                            </button>
                          </td>
                          <td className={num} />
                          <td className={num}>{sw.license_codes}</td>
                          <td className={num}>{sw.seats}</td>
                          <td className={num}>{sw.installed}</td>
                          <td className={num}>{sw.free}</td>
                          <td className="px-3 py-2">
                            <UsageBar seats={sw.seats} installed={sw.installed} />
                          </td>
                          <td className="px-3 py-2 whitespace-nowrap">
                            <UsageStatusBadge seats={sw.seats} installed={sw.installed} />
                          </td>
                        </tr>
                        {swOpen && (
                          <tr className="bg-gray-50/60">
                            <td colSpan={8} className="pl-14 pr-3 pb-3 pt-1">
                              <CodesTable codes={sw.codes} />
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    );
                  })}
              </React.Fragment>
            );
          })}
        </tbody>
        {totals && (
          <tfoot className="bg-gray-50 border-t-2 border-gray-200">
            <tr className="font-semibold text-gray-900">
              <td className="px-3 py-2.5">Total</td>
              <td className={num} />
              <td className={num}>{totals.license_codes}</td>
              <td className={num}>{totals.seats}</td>
              <td className={num}>{totals.installed}</td>
              <td className={num}>{totals.free}</td>
              <td className="px-3 py-2.5 text-sm tabular-nums" colSpan={2}>
                {getUsagePercent(totals.seats, totals.installed)} % de uso
              </td>
            </tr>
          </tfoot>
        )}
      </table>
    </div>
  );
};
